import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AbsenceDetail from './AbsenceDetail';
import EditAbsencePage from './EditAbsencePage';
import { FaArrowLeft, FaArrowRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

function AbsenceTracker() {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditPage, setShowEditPage] = useState(false);
  const [showDetailPage, setShowDetailPage] = useState(false);

  useEffect(() => {
    const fetchAbsences = async () => {
      try {
        const absencesCollection = collection(db, 'staffabsences');
        const absencesSnapshot = await getDocs(absencesCollection);
        const absencesList = absencesSnapshot.docs.map(doc => {
          const data = doc.data();
          let dateObj;
          if (data.date && typeof data.date.toDate === 'function') {
            dateObj = data.date.toDate();
          } else if (data.date && data.date.seconds) {
            dateObj = new Date(data.date.seconds * 1000);
          } else if (data.date) {
            dateObj = new Date(data.date);
          } else {
            dateObj = new Date(0); // Default to epoch if no date
          }
          
          return {
            id: doc.id,
            ...data,
            date: dateObj,
            formattedDate: dateObj.toLocaleDateString(),
            isExcused: data.reason === "Vacation" ? true : (data.isExcused || false),
            vacation: data.reason === "Vacation"
          };
        });

        // Sort the absences by date, newest to oldest
        absencesList.sort((a, b) => b.date.getTime() - a.date.getTime());

        setAbsences(absencesList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching absences: ", error);
        setLoading(false);
      }
    };

    fetchAbsences();
  }, []);

  const handleViewAbsences = (staff) => {
    setSelectedStaff(staff);
    setShowDetailPage(true);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleEdit = (absence) => {
    setEditingAbsence(absence);
    setShowEditPage(true);
  };

  const handleSaveEdit = async (editedAbsence) => {
    try {
      if (editedAbsence.reason === "Vacation") {
        editedAbsence.isExcused = true;
        editedAbsence.vacation = true;
      }

      const absenceRef = doc(db, 'staffabsences', editedAbsence.id);
      await updateDoc(absenceRef, editedAbsence);
      setAbsences(absences.map(absence => 
        absence.id === editedAbsence.id ? editedAbsence : absence
      ));
      setEditingAbsence(null);
      setShowEditPage(false);
    } catch (error) {
      console.error("Error updating absence: ", error);
    }
  };

  const handleCloseEditPage = () => {
    setEditingAbsence(null);
    setShowEditPage(false);
  };

  const handleCloseDetailPage = () => {
    setSelectedStaff(null);
    setShowDetailPage(false);
  };

  const handleDelete = async (absenceId) => {
    if (window.confirm('Are you sure you want to delete this absence record?')) {
      try {
        await deleteDoc(doc(db, 'staffabsences', absenceId));
        setAbsences(absences.filter(absence => absence.id !== absenceId));
      } catch (error) {
        console.error("Error deleting absence: ", error);
      }
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (event) => {
    setEntriesPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev * entriesPerPage < filteredAndSortedAbsences.length ? prev + 1 : prev));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const filteredAndSortedAbsences = absences
    .filter(absence => absence.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortConfig.key === 'date') {
        return sortConfig.direction === 'ascending' 
          ? a.date.getTime() - b.date.getTime()
          : b.date.getTime() - a.date.getTime();
      } else if (sortConfig.key !== null) {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
      }
      return 0;
    });

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredAndSortedAbsences.slice(indexOfFirstEntry, indexOfLastEntry);

  return (
    <div className="AbsenceTracker p-4">
      <h1 className="text-3xl font-serif mb-4">Absence Tracker</h1>
      {loading ? (
        <p className="text-center">Loading...</p>
      ) : showEditPage ? (
        <EditAbsencePage
          absence={editingAbsence}
          onSave={handleSaveEdit}
          onClose={handleCloseEditPage}
        />
      ) : showDetailPage ? (
        <AbsenceDetail
          staffName={selectedStaff}
          onClose={handleCloseDetailPage}
        />
      ) : (
        <>
          <div className="mb-4 flex justify-between items-center">
            <div className="flex items-center">
              <label className="mr-2">Entries per page:</label>
              <select
                value={entriesPerPage}
                onChange={handleEntriesPerPageChange}
                className="px-2 py-1 border border-gray-300 rounded"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="flex items-center ml-4">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
                className="px-4 py-2 border border-gray-300 rounded"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border-gray-200 shadow-md rounded-lg">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="py-2 px-4 border-b border-gray-200 text-center">#</th>
                  {['name', 'date', 'reason', 'isExcused', 'vacation'].map((column) => (
                    <th 
                      key={column}
                      className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer"
                      onClick={() => handleSort(column)}
                    >
                      <div className="flex items-center justify-center">
                        {column === 'name' ? 'Staff Name' : column.charAt(0).toUpperCase() + column.slice(1)}
                        {getSortIcon(column)}
                      </div>
                    </th>
                  ))}
                  <th className="py-2 px-4 border-b border-gray-200 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {currentEntries.map((absence, index) => (
                  <tr key={absence.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{index + 1 + indexOfFirstEntry}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{absence.name}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{absence.formattedDate}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{absence.reason}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{absence.isExcused ? 'Yes' : 'No'}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{absence.vacation ? 'Yes' : 'No'}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">
                      <button 
                        className="mr-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={() => handleViewAbsences(absence.name)}
                      >
                        View
                      </button>
                      <button 
                        className="mr-2 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={() => handleEdit(absence)}
                      >
                        Edit
                      </button>
                      <button 
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={() => handleDelete(absence.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between mt-4">
            <div className="flex space-x-2">
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <FaArrowLeft />
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleNextPage}
                disabled={indexOfLastEntry >= filteredAndSortedAbsences.length}
              >
                <FaArrowRight />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AbsenceTracker;